<template>
    <div>
        <div class="mb-3 flex justify-end gap-x-3">
            <hts-form-button
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddBusiness">
                {{ $t('labels.businessModule.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="tables"
            :headers="headers"
            :server="apiGetBusinesses"
            :auto-size="autoSizeTable"
            :use-archive="useBusinessArchive"
            :use-restore="useBusinessRestore"
            :use-delete="true"
            :delete-function="handleDelete"
            use-selection
            store-name="businessDataTable"
            store-key="Business"
            @selection-changed="onSelectionChanged" />
        <add-business
            ref="addBusiness"
            @refresh-tables="refreshTables()" />

        <edit-business
            ref="editBusiness"
            @refresh-tables="refreshTables()" />

        <delete-business
            :show="deleteBusiness"
            :business="businessSelected"
            @refresh-tables="refreshTables()"
            @close="closeDelete" />

        <delete-business
            :show="deleteBusinesses"
            :business="businessIdMultiSelected"
            @refresh-tables="refreshTables()"
            @reset-business-selecteds="resetBusinessSelecteds()"
            @close="closeDeletes" />

        <detail-business ref="detailBusiness" />
    </div>
</template>

<script lang="ts" setup>
import { IBusiness } from './ignore/imports/form'
import TasksCell from './ignore/tables/tasks-name.vue'
import NodeTypeCell from './ignore/tables/node-type-name.vue'
import AddBusiness from './ignore/businesses/add.vue'
import EditBusiness from './ignore/businesses/edit.vue'
import DetailBusiness from './ignore/businesses/detail.vue'
import DeleteBusiness from './ignore/businesses/delete.vue'
const apiGetBusinesses = $urlSystem.business['get-list']

const { $formatters } = useNuxtApp()

const tables = ref()
const addBusiness = ref()
const editBusiness = ref()
const detailBusiness = ref()
const deleteBusiness = ref<Boolean>(false)
const deleteBusinesses = ref<Boolean>(false)
const viewDetail = ref<Boolean>(false)
const businessSelected = ref<any>()
const transportTypes = ref([])
const nodeAndWareHouseTypes = ref([])
const businessMultiSelected = ref<Array<IBusiness> | null>()
const addDefaultLoading = ref(false)

const handleAddBusiness = () => {
  if (addBusiness.value) {
    addBusiness.value.open()
  }
}

const handleEdit = (business: IBusiness) => {
  if (editBusiness.value) {
    editBusiness.value.open(business._id)
  }
}

const handleDelete = (deleteOptions: any) => {
  if (deleteOptions && deleteOptions._id) {
    businessSelected.value = {
      selectedItems: [deleteOptions._id]
    }
  } else {
    businessSelected.value = deleteOptions
  }
  deleteBusiness.value = true
}

const closeDelete = () => {
  deleteBusiness.value = false
}

const closeDeletes = () => {
  deleteBusinesses.value = false
}

const handleViewDetail = (business: IBusiness) => {
  if (detailBusiness.value) {
    detailBusiness.value.open(business._id)
  }
}

const headers = [
  {
    field: 'name',
    headerName: $t('labels.businessModule.name'),
    sortable: true,
    filter: 'agTextColumnFilter',
    minWidth: 150
  },
  {
    field: 'tasks',
    headerName: $t('labels.taskList'),
    cellRenderer: TasksCell,
    autoHeight: true,
    minWidth: 150
  },
  {
    field: 'nodeTypes',
    headerName: $t('labels.nodeType'),
    cellRenderer: NodeTypeCell,
    autoHeight: true,
    minWidth: 150
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    minWidth: 150
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    minWidth: 180,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    minWidth: 180,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    suppressMenu: true,
    resizable: false,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'Actions',
    cellRendererParams: {
      storeKey: 'businessDataTable',
      storeName: 'Business',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          isAll: true,
          click: handleViewDetail
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          click: handleEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: true,
          isArchive: true,
          click: handleDelete
        }
      ]
    }
  }
]

const autoSizeTable = {
  active: false,
  skipHeader: false
}

const refreshTables = () => {
  if (tables.value) {
    tables.value.refresh()
  }
}

const addDefault = $lodash.debounce(() => {
  const api = $urlSystem.business.default
  addDefaultLoading.value = true
  $api(api)
    .then((data) => {
      if (!data) {
        return
      }
      const resData: any = data?.data?.value

      if (resData?.success) {
        process.client && $toast().success($t('labels.addSuccess'))
        refreshTables()
      }
      addDefaultLoading.value = false
    })
    .catch((error: any) => {
      addDefaultLoading.value = false
      return error
    })
}, 300)

const resetBusinessSelecteds = () => {
  businessMultiSelected.value = null
}

const onSelectionChanged = (business: Array<IBusiness> | null) => {
  businessMultiSelected.value = business
}

const businessIdMultiSelected = computed(() => {
  if (!businessMultiSelected.value || !Array.isArray(businessMultiSelected.value)) {
    return []
  }
  return businessMultiSelected.value.map((business) => business._id)
})

const useBusinessArchive = computed(() => {
  return { api: $urlSystem.business.archive, permissions: true }
})
const useBusinessRestore = computed(() => {
  return { api: $urlSystem.business.restore, permissions: true }
})
</script>
